import {useState, useCallback, useMemo, useRef} from 'react';
import {getMonolithApiHeaders} from '@udemy-v2/api-fetch';
import getShareStrings from '../config';

import {createNetworkShareBehaviors} from '../behaviors';
import {useI18n} from '@udemy-v2/i18n';
import {ShareableObject, ChannelType} from '../types';
import {stripLocale} from '@/app/lib/core/strip-locale';

interface ShareHookProps {
    shareableObject: ShareableObject;
    sourceUrl: string;
    context: string;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function useSocialShare({shareableObject, sourceUrl, context}: ShareHookProps) {
    // State
    const [isImportEmailModalShown, setIsImportEmailModalShown] = useState(false);

    const [isShareModalShown, setIsShareModalShown] = useState(false);
    const [isCopyLinkShown, setIsCopyLinkShown] = useState(false);
    const [showEmailFeedback, setShowEmailFeedback] = useState(false);
    const [sharedUrl, setSharedUrl] = useState('');
    const [loadingNetworks, setLoadingNetworks] = useState<Record<string, boolean>>({});
    const shareUrlPromiseRef = useRef<Promise<string> | null>(null);

    const {gettext, interpolate} = useI18n();
    const networkShareBehaviors = useMemo(() => createNetworkShareBehaviors(), []);

    const shareContext = useMemo(() => {
        const strings = getShareStrings(gettext);
        return {
            shareableText: interpolate(
                strings[context].shareableText,
                {
                    title: shareableObject.title,
                },
                true,
            ),
            title: shareableObject.title,
        };
    }, [context, gettext, interpolate, shareableObject.title]);

    // Modal Management
    const showImportEmailModal = useCallback(() => {
        setIsImportEmailModalShown(true);
        setShowEmailFeedback(false);
    }, []);

    const hideImportEmailModal = useCallback(() => {
        setIsImportEmailModalShown(false);
    }, []);

    // Copy Link Management
    const hideCopyText = useCallback(() => {
        setIsCopyLinkShown(false);
    }, []);

    const showCopyText = useCallback(() => {
        setIsCopyLinkShown(true);
        setTimeout(hideCopyText, 3000);
    }, [hideCopyText]);

    // Email Feedback
    const callbackEmails = useCallback(() => {
        setIsImportEmailModalShown(false);
        setShowEmailFeedback(true);
    }, []);

    const shareActions = useMemo(
        () => ({
            showImportEmailModal,
            showCopyText,
        }),
        [showImportEmailModal, showCopyText],
    );

    // URL Management
    const getOrPopulateUrl = useCallback(async () => {
        // If there's already a request in progress, return that promise
        if (shareUrlPromiseRef.current) {
            return shareUrlPromiseRef.current;
        }

        if (sharedUrl) {
            return sharedUrl;
        }

        // Create and store the promise
        const promise = (async () => {
            try {
                const response = await fetch('/api-2.0/share/', {
                    method: 'POST',
                    headers: {
                        ...getMonolithApiHeaders(),
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({
                        target:
                            // sourceUrl
                            // TODO Figure out how to get the equivalent of UdLink.to() in udemy-v2
                            // ? UdLink.to(sourceUrl)
                            // :
                            process.env.NODE_ENV === 'development'
                                ? (() => {
                                      const url = new URL(window.location.href);
                                      url.protocol = 'https:';
                                      url.hostname = 'www.udemy.dev';
                                      url.port = '';
                                      return stripLocale(url.toString());
                                  })()
                                : stripLocale(
                                      window.location.href.replace(window.location.search, ''),
                                  ),
                        context,
                    }),
                });

                if (!response.ok) {
                    throw new Error(
                        `Failed to create share URL: ${response.status} ${response.statusText}`,
                    );
                }

                const data: {url: string; target: string} = await response.json();
                setSharedUrl(data.url);
                return data.url;
            } finally {
                shareUrlPromiseRef.current = null; // Clear the promise after completion
            }
        })();

        shareUrlPromiseRef.current = promise;
        return promise;
    }, [sharedUrl, context]);

    // Share Actions
    const share = useCallback(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        async (channel: ChannelType, onError = (error: Error) => {}) => {
            setLoadingNetworks((prev) => ({...prev, [channel]: true}));
            try {
                const url = await getOrPopulateUrl();
                const behavior = networkShareBehaviors[channel];

                if (behavior) {
                    behavior(url, shareContext, shareActions);
                }
                setLoadingNetworks((prev) => ({...prev, [channel]: false}));
            } catch (error) {
                setLoadingNetworks((prev) => ({...prev, [channel]: false}));
                onError(error as Error);
            }
        },
        [getOrPopulateUrl, networkShareBehaviors, shareContext, shareActions],
    );

    // Loading network actions
    const clearLoadingNetwork = useCallback((channel: ChannelType) => {
        setLoadingNetworks((prev) => ({...prev, [channel]: false}));
    }, []);

    const setLoadingNetwork = useCallback((channel: ChannelType) => {
        setLoadingNetworks((prev) => ({...prev, [channel]: true}));
    }, []);

    // Modal actions
    const showShareModal = useCallback(() => {
        setIsShareModalShown(true);
    }, []);

    const hideShareModal = useCallback(() => {
        setIsShareModalShown(false);
    }, []);

    return {
        // State
        isImportEmailModalShown,
        isShareModalShown,
        isCopyLinkShown,
        showEmailFeedback,
        sharedUrl,
        loadingNetworks,

        // Actions
        share,
        showImportEmailModal,
        hideImportEmailModal,
        showCopyText,
        callbackEmails,
        getOrPopulateUrl,
        clearLoadingNetwork,
        setLoadingNetwork,
        showShareModal,
        hideShareModal,
        hideCopyText,

        // Computed
        sourcePage: context,
    };
}
